import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Select, DatePicker } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SlackOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  GetProductDataAPI,
  DeleteBreakageEntryDataAPI,
  CreateBreakageEntryDataAPI,
  UpdateBreakageEntryDataAPI,
  GetBreakageEntryDataAPI,
} from '../../AllAPI/AdminAPI'

const BreakageEntry = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  const [breakage, setBreakage] = useState([])
  const [products, setProducts] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [addBtnLoading, setAddBtnLoading] = useState(false)

  /* ================= Modal + Inputs ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [date, setDate] = useState(null)
  const [productId, setProductId] = useState(null)
  const [breakageQty, setBreakageQty] = useState('')

  /* ================= Delete ================= */
  const [deleteId, setDeleteId] = useState(null)

  useEffect(() => {
    fetchProducts()
    fetchBreakageEntryData()
  }, [])

  /* ================= Fetch Breakage Entry Data ================= */
  const fetchBreakageEntryData = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(
        `${GetBreakageEntryDataAPI}/${selectedYear}`,
      )
      setBreakage(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load breakage entry data')
    } finally {
      setLoading(false)
    }
  }

  /* ================= Fetch Products ================= */
  const fetchProducts = async () => {
    try {
      const res = await axiosInstance.get(
        `${GetProductDataAPI}/${selectedYear}`,
      )
      setProducts(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load products')
    }
  }

  /* ================= Open Modal ================= */
  const openModal = (record = null) => {
    if (record) {
      setEditingId(record.id)
      setDate(dayjs(record.date))
      setProductId(record.prod_id)
      setBreakageQty(record.breakage_quantity)
    } else {
      setEditingId(null)
      setDate(null)
      setProductId(null)
      setBreakageQty('')
    }
    setIsModalOpen(true)
  }

  /* ================= Save / Update ================= */
  const handleSave = async () => {
    if (!date) return toast.error('Date is required')
    if (!productId) return toast.error('Product is required')
    if (!breakageQty || Number(breakageQty) <= 0)
      return toast.error('Enter valid breakage quantity')

    const selectedProduct = products.find((p) => p.prod_id === productId)

    const payload = {
      date: date.format('YYYY-MM-DD'),
      prod_id: productId,
      breakage_quantity: Number(breakageQty),
      year: selectedYear,
    }

    try {
      setSubmitLoading(true)
      if (editingId) {
        const res = await axiosInstance.put(
          `${UpdateBreakageEntryDataAPI}/${editingId}`,
          payload,
        )
        if (res.data === 'UPDATED') toast.success('Updated successfully')
        if (res.data === 'NOTVALID') toast.warn('Breakage qty not valid')
      } else {
        const res = await axiosInstance.post(
          CreateBreakageEntryDataAPI,
          payload,
        )
        if (res.data === 'SAVED') toast.success('Saved successfully')
        if (res.data === 'NOTVALID') toast.warn('Breakage qty not valid')
      }

      fetchBreakageEntryData()
      setIsModalOpen(false)
    } catch {
      toast.error('Save failed')
    } finally {
      setSubmitLoading(false)
    }
  }

  /* ================= Delete ================= */
  // const confirmDelete = async () => {
  //   try {
  //     const res = await axiosInstance.delete(
  //       `${DeleteBreakageEntryDataAPI}/${deleteId}`,
  //     )
  //     if (res.data === 'DELETED') {
  //       toast.success('Breakage Entry deleted successfully')
  //       setDeleteId(null)
  //       fetchBreakageEntryData()
  //     }
  //   } catch {
  //     // toast.error('Delete failed')
  //   }
  // }
  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)

      const res = await axiosInstance.delete(
        `${DeleteBreakageEntryDataAPI}/${deleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Breakage Entry deleted successfully')
        setDeleteId(null)
        fetchBreakageEntryData()
      }
    } catch {
      toast.error('Delete failed')
    } finally {
      setDeleteLoading(false)
      setActionLoadingId(null)
    }
  }

  /* ================= Search ================= */
  const filteredEntry = breakage.filter((s) =>
    s.product?.product_name?.toLowerCase().includes(searchText.toLowerCase()),
  )

  /* ================= Table ================= */
  const columns = [
    {
      title: 'Sr No',
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },
    {
      title: 'Product',
      render: (_, record) => (
        <>
          <div>
            {record.product?.product_name || '-'} &nbsp;{' '}
            <small style={{ color: '#888' }}>
              ({record.product?.size || ''}){' '}
            </small>
          </div>
        </>
      ),
    },
    {
      title: 'Breakage Qty',
      dataIndex: 'breakage_quantity',
      render: (value) => (
        <div style={{ textAlign: 'right' }}>{Number(value)}</div>
      ),
    },

    // {
    //   title: 'Actions',
    //   render: (_, record) => (
    //     <Space>
    //       <Button
    //         icon={<EditOutlined />}
    //         size="small"
    //         title="Edit"
    //         onClick={() => openModal(record)}
    //       />
    //       <Button
    //         icon={<DeleteOutlined />}
    //         size="small"
    //         title="Delete"
    //         danger
    //         onClick={() => setDeleteId(record.id)}
    //       />
    //     </Space>
    //   ),
    // },

    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            title="Edit"
            loading={actionLoadingId === `edit-${record.id}`}
            onClick={() => {
              setActionLoadingId(`edit-${record.id}`)
              openModal(record)
              setActionLoadingId(null)
            }}
          />

          <Button
            icon={<DeleteOutlined />}
            size="small"
            title="Delete"
            danger
            loading={actionLoadingId === `delete-${record.id}`}
            onClick={() => {
              setActionLoadingId(`delete-${record.id}`)
              setDeleteId(record.id)
            }}
          />
        </Space>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-raw">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Breakage Entry
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            loading={addBtnLoading}
            onClick={() => {
              setAddBtnLoading(true)
              openModal()
              setAddBtnLoading(false)
            }}
          >
            Add
          </Button>

          <Input
            placeholder="Search by product..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredEntry}
          rowKey="id"
          loading={loading}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        title={editingId ? 'Edit Entry' : 'Add Entry'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSave}
        okText={editingId ? 'Update' : 'Save'}
        okButtonProps={{
          loading: submitLoading,
          className: 'receipt-ok-btn',
        }}
      >
        <label>Date</label>
        <DatePicker value={date} onChange={setDate} style={{ width: '100%' }} />

        <label className="mt-2">Product</label>
        <Select
          value={productId}
          onChange={setProductId}
          placeholder="Select product"
          style={{ width: '100%' }}
        >
          {products.map((p) => (
            <Select.Option key={p.prod_id} value={p.prod_id}>
              {p.product_name} &nbsp; ({p.size})
            </Select.Option>
          ))}
        </Select>

        <label className="mt-2">Breakage Qty</label>
        <Input
          type="number"
          min={0}
          value={breakageQty}
          onChange={(e) => setBreakageQty(e.target.value)}
        />
      </Modal>

      {/* ================= DELETE ================= */}
      <Modal
        title="Confirm Delete"
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onOk={confirmDelete}
        okText="Delete"
        okButtonProps={{
          danger: true,
          loading: deleteLoading,
        }}
      >
        Are you sure you want to delete this breakage entry?
      </Modal>
    </>
  )
}

export default BreakageEntry
